// ==========================================================================
// components/NetWorth.tsx — the family net worth card on Home (10 Oct 2026)
//   Family view, or one person: what we own less what we owe, what makes it
//   up (click a row for the detail), each person's share, and what isn't
//   counted yet. Colours: chart-3 / chart-5 / chart-2 / blue #3784C6 for
//   property, shares, cash and vehicles (checked for colour blindness; every bar is also labelled); debts in
//   the muted ink so they never read as a series.
// ==========================================================================
import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Info, Users } from "lucide-react";
import { Card } from "@/components/ui";
import { money } from "@/lib/money";
import { tokenColour } from "@/lib/calendar";
import { NW_LABEL, sumBy, useNetWorth, type NwCategory } from "@/lib/networth";

const COLOUR: Record<NwCategory, string> = { property: "chart-3", shares: "chart-5", cash: "chart-2", vehicles: "#3784C6", debts: "text-muted" };
const col = (c: NwCategory, a = 1) => (COLOUR[c].startsWith("#") ? COLOUR[c] : tokenColour(COLOUR[c], a));
const ORDER: NwCategory[] = ["property", "shares", "cash", "vehicles", "debts"];
const LINK: Record<NwCategory, string> = { property: "/property", shares: "/investments", cash: "/money/settings/accounts", vehicles: "/vehicles", debts: "/money/settings/accounts" };

export default function NetWorth() {
  const nw = useNetWorth();
  const [who, setWho] = useState<string>("family");
  const [open, setOpen] = useState<NwCategory | null>(null);
  if (nw.failed.length) return <Card><div role="alert" className="space-y-3">
    <h2 className="text-lg font-semibold">Net worth is unavailable</h2>
    <p className="text-sm text-ink-muted">We couldn't load: {nw.failed.join(", ")}. No total is shown because it could leave out assets or debts.</p>
    <button type="button" disabled={nw.refreshing} onClick={() => void nw.retry()} className="rounded-md bg-primary px-4 py-2 font-semibold text-primary-ink disabled:opacity-60">{nw.refreshing ? "Retrying…" : "Try again"}</button>
  </div></Card>;
  if (nw.loading) return <Card><p role="status" className="text-ink-muted">Loading all net-worth sources…</p></Card>;

  const owners = nw.entities.filter((e) => nw.items.some((i) => i.owner === e.id));
  const items = who === "family" ? nw.items : nw.items.filter((i) => i.owner === who);
  const assets = items.filter((i) => i.cents > 0).reduce((t, i) => t + i.cents, 0);
  const debts = -items.filter((i) => i.cents < 0).reduce((t, i) => t + i.cents, 0);
  const total = assets - debts;
  const scale = Math.max(1, assets, debts);
  const name = (id: string) => nw.entities.find((e) => e.id === id)?.name ?? "—";

  return (
    <Card className="nw-card space-y-5">
      <div className="nw-summary flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-sm text-ink-muted">{nw.missing.length ? "Known net worth · incomplete" : "Estimated net worth"}{who === "family" ? ", the family" : `, ${name(who)}`}</h2>
          {nw.refreshing && <p role="status" className="text-xs text-ink-muted">Updating · showing the last loaded values</p>}
          <div className={`nw-value num font-heading text-4xl font-semibold lg:text-5xl ${total < 0 ? "text-bad" : ""}`}>{money(Math.round(total / 100) * 100)}</div>
          <div className="mt-1 text-sm text-ink-muted">Own <span className="num font-medium text-ink">{money(assets)}</span> · owe <span className="num font-medium text-ink">{money(debts)}</span></div>
        </div>
        <div className="nw-owner-tabs flex flex-wrap gap-2">
          {[{ id: "family", name: "Family" }, ...owners].map((o) => (
            <button key={o.id} type="button" aria-pressed={who === o.id} onClick={() => { setWho(o.id); setOpen(null); }}
              className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm transition ${who === o.id ? "bg-ink font-semibold text-bg" : "bg-surface-raised text-ink-muted hover:text-ink"}`}>
              {o.id === "family" && <Users className="h-4 w-4" />}{o.name}
            </button>
          ))}
        </div>
      </div>

      <div className="nw-breakdown space-y-2">
        {ORDER.map((c) => {
          const v = Math.abs(sumBy(items, c)); const rows = items.filter((i) => i.category === c);
          if (!rows.length) return null;
          return (
            <div key={c}>
              <button type="button" aria-expanded={open === c} onClick={() => setOpen(open === c ? null : c)} className="w-full text-left" title={`${NW_LABEL[c]}: ${money(v)}`}>
                <div className="flex items-baseline justify-between text-sm">
                  <span className="flex items-center gap-1.5 font-medium">{NW_LABEL[c]} <ChevronDown className={`h-3.5 w-3.5 text-ink-muted transition ${open === c ? "rotate-180" : ""}`} /></span>
                  <span className="num font-semibold">{c === "debts" ? "−" : ""}{money(v)}</span>
                </div>
                <div className="mt-1 h-3 overflow-hidden rounded-full bg-surface-raised">
                  <div className="h-full rounded-full" style={{ width: `${Math.max(1.5, (v / scale) * 100)}%`, background: col(c, c === "debts" ? 0.45 : 1) }} />
                </div>
              </button>
              {open === c && (
                <ul className="mt-2 space-y-1 rounded-md bg-surface-raised/60 p-3 text-sm">
                  {rows.sort((a, b) => Math.abs(b.cents) - Math.abs(a.cents)).map((i) => (
                    <li key={i.key} className="flex justify-between gap-3"><span>{i.name}{who === "family" && owners.length > 1 ? <span className="text-ink-muted"> · {name(i.owner)}</span> : null}{i.note && <span className="text-xs text-ink-muted"> · {i.note}</span>}</span><span className="num">{money(i.cents)}</span></li>
                  ))}
                  <li className="pt-1 text-xs"><Link to={LINK[c]} className="text-primary">Open {NW_LABEL[c].toLowerCase()}</Link></li>
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {who === "family" && owners.length > 1 && (
        <div className="nw-owners grid gap-3 border-t border-line pt-4 sm:grid-cols-2 lg:grid-cols-3">
          {owners.map((o) => {
            const mine = nw.items.filter((i) => i.owner === o.id); const t = sumBy(mine);
            return (
              <button key={o.id} type="button" onClick={() => setWho(o.id)} className="rounded-lg bg-surface-raised/60 p-3 text-left transition hover:bg-surface-raised">
                <div className="text-sm text-ink-muted">{o.name}</div>
                <div className={`num text-xl font-semibold ${t < 0 ? "text-bad" : ""}`}>{money(t)}</div>
                <div className="mt-1 flex h-1.5 overflow-hidden rounded-full bg-surface">
                  {(["property", "shares", "cash", "vehicles"] as NwCategory[]).map((c) => { const v = sumBy(mine, c); const pos = mine.filter((i) => i.cents > 0).reduce((s, i) => s + i.cents, 0) || 1;
                    return v > 0 ? <span key={c} title={`${NW_LABEL[c]} ${money(v)}`} style={{ width: `${(v / pos) * 100}%`, background: col(c) }} className="border-r-2 border-surface last:border-0" /> : null; })}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {nw.missing.length > 0 && (
        <p className="nw-missing flex gap-2 text-xs text-ink-muted"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>Not counted yet: {nw.missing.map((m) => `${m.name} (${m.why})`).join(", ")}. A novated lease isn't counted (the lease company owns the car). Balances come from the latest statement or CSV; property from your value estimate.</span></p>
      )}
    </Card>
  );
}
